import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Grid,
  Autocomplete,
  TextField,
  ThemeProvider,
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { createTheme, styled } from "@mui/material/styles";
import InsightsIcon from "@mui/icons-material/Insights";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SalesRepForm from "./SalesRepForm";
import { getSalesReps, getSalesRep } from "../API/salesRepAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));


function SalesRepList() {
  const navigate = useNavigate();
  const [salesReps, setSalesReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    branch: "QC",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  const fetchSalesReps = useCallback(async () => {
    setIsLoading(true);
    try {
      const reps = await getSalesReps();
      if (reps) {
        const sortedReps = reps.sort((a, b) =>
          a.lastname.toLowerCase().localeCompare(b.lastname.toLowerCase())
        );
        setSalesReps(sortedReps);
      }
    } catch (error) {
      setErrorMessage("Failed to fetch sales representatives.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalesReps();
  }, [fetchSalesReps]);

  const handleSearchClick = async () => {
    if (!selectedRep) {
      setErrorMessage("Please select a sales representative.");
      return;
    }
    setIsLoading(true);
    try {
      const salesRepData = await getSalesRep(selectedRep.id);
      if (salesRepData) {
        setFormData({
          firstname: salesRepData.firstname || "",
          lastname: salesRepData.lastname || "",
          branch: salesRepData.branch || "QC",
        });
        setIsFormVisible(true);
        setIsEdit(false); // Start in view mode
      }
    } catch (error) {
      setErrorMessage("Failed to fetch sales representative data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEdit((prev) => !prev); // Toggle edit mode
  };

  const handleBackOrCancel = () => {
    setIsFormVisible(false); // Hide the form
    setIsEdit(false);        // Ensure edit mode is reset
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const submitData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        branch: formData.branch,
        user_update: "admin",
      };

      const response = await fetch(
        `https://vlz2vlcl73.execute-api.ap-southeast-1.amazonaws.com/prod/putSalesRep/${selectedRep.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submitData),
        }
      );

      if (response.ok) {
        setSuccessMessage("Sales Representative updated successfully!");
        setIsEdit(false); // Lock the form after updating
        fetchSalesReps(); // Refresh the sales rep list
        setIsFormVisible(false); // Hide the form after submission
      } else {
        const result = await response.json();
        setErrorMessage(
          result.message ||
          "An unexpected error occurred while updating the Sales Representative. Please try again later."
        );
      }
    } catch (error) {
      setErrorMessage(
        "An error occurred while updating the Sales Representative. Please check your network connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutocompleteChange = (event, newValue) => {
    setSelectedRep(newValue);
    if (!newValue) {
      setIsFormVisible(false); // Hide the form when the Autocomplete is cleared
    }
  };

  return (

    <BaseWrapper
      titleIcon={<SupervisorAccountIcon />}
      title="Sales"
      subTitle="Sales Representative"
      subTitleRoute={"/manage-salesreps"}
      createButtonLabel="Create Sales Representative"
      onClickCreateButton={() => navigate("/create-salesrep")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", marginTop: "16px" }}>
          <CircularProgress size={24} />
          <Typography sx={{ marginLeft: "8px" }}>Loading...</Typography>
        </Box>
      ) : (
        <>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Autocomplete
                  freeSolo
                  options={salesReps}
                  getOptionLabel={(option) =>
                    `${option.lastname}, ${option.firstname}`
                  }
                  value={selectedRep}
                  onChange={handleAutocompleteChange} // Handle changes including clearing
                  renderInput={(params) => (
                    <TextField {...params} label="Sales Representative" variant="outlined" size="small" />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                direction={{ xs: "column", sm: "row" }}
                container
                alignItems="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearchClick}
                  disabled={isLoading || !selectedRep}
                  sx={{
                    mb: { xs: 0, md: 0, sm: 2 },
                    mr: { xs: 0, md: 3, sm: 3 },
                  }}
                >
                  {isLoading ? "Searching..." : "Search"}{" "}
                </Button>
                {isFormVisible && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditClick}
                  >
                    {isEdit ? "LOCK RECORD" : "EDIT RECORD"}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      <StyledHr />

      {isFormVisible && (
        <SalesRepForm
          initialValues={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit} // Pass handleSubmit to SalesRepForm
          isLoading={isLoading}
          errors={errors}
          successMessage={successMessage}
          setSuccessMessage={setSuccessMessage}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          isUpdate={isEdit}
          isView={!isEdit}
          onCancel={handleBackOrCancel}
        />
      )}
    </BaseWrapper>

    // <ThemeProvider theme={theme}>
    //   {/* Success Snackbar */}
    //   <Snackbar
    //     open={!!successMessage}
    //     autoHideDuration={3000}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setSuccessMessage("")} // Clear the success message on close
    //   >
    //     <Alert
    //       severity="success"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {successMessage}
    //     </Alert>
    //   </Snackbar>

    //   {/* Loading Snackbar */}
    //   <Snackbar
    //     open={isLoading}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //   >
    //     <Alert
    //       severity="info"
    //       icon={<CircularProgress size={18} />}
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       Loading, please wait...
    //     </Alert>
    //   </Snackbar>

    //   {/* Error Snackbar */}
    //   <Snackbar
    //     open={!!errorMessage}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setErrorMessage("")}
    //   >
    //     <Alert
    //       severity="error"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {errorMessage}
    //     </Alert>
    //   </Snackbar>

    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <InsightsIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   color: "white",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Sales</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>Sales</Typography>
    //               <Typography
    //                 component={Link}
    //                 to="/manage-salesreps"
    //                 sx={{ color: "#ffffff", textDecoration: "none" }}
    //               >
    //                 Sales Representatives
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />
    //       <CardContent sx={{ padding: "20px" }}>          
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Sales Representatives
    //               </Typography>
    //             }
    //             action={
    //               <Button
    //                 component={Link}
    //                 onClick={() => navigate("/create-salesrep")}
    //                 variant="contained"
    //                 color="primary"
    //                 startIcon={<AddIcon />}
    //               >
    //                 Create Sales Representative
    //               </Button>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />

    //           {isLoading ? (
    //             <Box sx={{ display: "flex", alignItems: "center", marginTop: "16px" }}>
    //               <CircularProgress size={24} />
    //               <Typography sx={{ marginLeft: "8px" }}>Loading...</Typography>
    //             </Box>
    //           ) : (
    //             <>
    //               <Grid item xs={12}>
    //                 <Grid container spacing={3} sx={{ mb: 2 }}>
    //                   <Grid item xs={4}>
    //                     <Autocomplete
    //                       freeSolo
    //                       options={salesReps}
    //                       getOptionLabel={(option) =>
    //                         `${option.lastname}, ${option.firstname}`
    //                       }
    //                       value={selectedRep}
    //                       onChange={handleAutocompleteChange} // Handle changes including clearing
    //                       renderInput={(params) => (
    //                         <TextField {...params} label="Sales Representative" variant="outlined" size="small" />
    //                       )}                          
    //                     />
    //                   </Grid>
    //                   <Grid
    //                     item
    //                     xs={12}
    //                     sm={6}
    //                     direction={{ xs: "column", sm: "row" }}
    //                     container
    //                     alignItems="center"
    //                   >
    //                     <Button
    //                       variant="contained"
    //                       color="primary"
    //                       onClick={handleSearchClick}
    //                       disabled={isLoading || !selectedRep}
    //                       sx={{
    //                         mb: { xs: 0, md: 0, sm: 2 },
    //                         mr: { xs: 0, md: 3, sm: 3 },
    //                       }}
    //                     >
    //                       {isLoading ? "Searching..." : "Search"}{" "}
    //                     </Button>                      
    //                     {isFormVisible && (
    //                       <Button
    //                         variant="contained"
    //                         color="primary"
    //                         onClick={handleEditClick}
    //                       >
    //                         {isEdit ? "LOCK RECORD" : "EDIT RECORD"}
    //                       </Button>
    //                     )}
    //                   </Grid>
    //                 </Grid>
    //               </Grid>                                 
    //             </>
    //           )}

    //           <StyledHr/>

    //           {isFormVisible && (
    //             <SalesRepForm
    //               initialValues={formData}
    //               setFormData={setFormData}
    //               onSubmit={handleSubmit} // Pass handleSubmit to SalesRepForm
    //               isLoading={isLoading}
    //               errors={errors}
    //               successMessage={successMessage}
    //               setSuccessMessage={setSuccessMessage}
    //               errorMessage={errorMessage}
    //               setErrorMessage={setErrorMessage}
    //               isUpdate={isEdit}
    //               isView={!isEdit}
    //               onCancel={handleBackOrCancel}
    //             />
    //           )} 

    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default SalesRepList;
